// Thin wrapper over card-live.ts для legacy artifact_card API.
// Новые surfaces используют card-live / rich_card напрямую.

import type { TutorArtifactCard, TutorRichCard } from "@studyhub/shared/contracts/tutor";
import {
	applyCardLivePatch,
	applyLiveRichCardsToMessages,
	bindRichCardAnchor,
	type CardLivePatch,
	getRichCardAnchor,
	isRichCardSettled,
	markRichCardSettled,
	mergeRichCardsWithLive,
	orphanRichCards,
	resetCardLiveForTests,
	trackRichCardPending,
} from "./card-live";
import { deepLinkForClientArtifact } from "./chat/artifact-card-links";

/** materialId → jobRef для glossary socket events. */
const glossaryJobByMaterialId = new Map<string, string>();

export type ArtifactCardLivePatch = Partial<
	Pick<TutorArtifactCard, "status" | "summary" | "deepLink" | "title" | "errorMessage">
>;

export function artifactCardToRich(card: TutorArtifactCard): TutorRichCard {
	return {
		type: "rich_card",
		kind: "artifact",
		id: card.jobRef,
		jobRef: card.jobRef,
		status: card.status,
		...(card.title ? { title: card.title } : {}),
		...(card.summary ? { summary: card.summary } : {}),
		...(card.deepLink ? { deepLink: card.deepLink } : {}),
		...(card.errorMessage ? { errorMessage: card.errorMessage } : {}),
		meta: {
			artifact: card.artifact,
			materialId: card.materialId,
		},
	};
}

export function richCardToArtifact(card: TutorRichCard): TutorArtifactCard | null {
	if (card.kind !== "artifact") return null;
	const artifact = card.meta?.artifact;
	const materialId = card.meta?.materialId;
	if (
		typeof artifact !== "string" ||
		!["notes", "flashcards", "quiz", "glossary"].includes(artifact) ||
		typeof materialId !== "string"
	) {
		return null;
	}
	const status =
		card.status === "ready" || card.status === "failed" || card.status === "pending"
			? card.status
			: "pending";
	return {
		type: "artifact_card",
		artifact: artifact as TutorArtifactCard["artifact"],
		jobRef: card.jobRef ?? card.id,
		materialId,
		status,
		...(card.title ? { title: card.title } : {}),
		...(card.summary ? { summary: card.summary } : {}),
		...(card.deepLink ? { deepLink: card.deepLink } : {}),
		...(card.errorMessage ? { errorMessage: card.errorMessage } : {}),
	};
}

export function trackArtifactCardPending(
	card: {
		jobRef: string;
		materialId: string;
		artifact: TutorArtifactCard["artifact"];
		title?: string;
	},
	opts?: { anchorMessageId?: string },
): void {
	if (card.artifact === "glossary") {
		glossaryJobByMaterialId.set(card.materialId, card.jobRef);
	}
	trackRichCardPending(
		artifactCardToRich({
			type: "artifact_card",
			artifact: card.artifact,
			jobRef: card.jobRef,
			materialId: card.materialId,
			status: "pending",
			...(card.title ? { title: card.title } : {}),
		}),
		opts,
	);
}

export function applyArtifactCardLivePatch(jobRef: string, patch: ArtifactCardLivePatch): void {
	applyCardLivePatch(jobRef, patch as CardLivePatch);
}

export function applyArtifactCardReadyByJobRef(
	jobRef: string,
	opts: {
		artifact: TutorArtifactCard["artifact"];
		summary?: string;
	},
): void {
	applyCardLivePatch(jobRef, {
		status: "ready",
		deepLink: deepLinkForClientArtifact(opts.artifact, jobRef),
		...(opts.summary ? { summary: opts.summary } : {}),
		errorMessage: undefined,
	});
}

export function applyArtifactCardFailedByJobRef(jobRef: string, errorMessage?: string): void {
	applyCardLivePatch(jobRef, {
		status: "failed",
		...(errorMessage ? { errorMessage } : {}),
	});
}

export function resolveGlossaryJobRef(materialId: string): string | undefined {
	return glossaryJobByMaterialId.get(materialId);
}

/** Мержит persisted/stream artifact cards с live-патчами. */
export function mergeArtifactCardsWithLive(cards: TutorArtifactCard[]): TutorArtifactCard[] {
	if (cards.length === 0) return cards;
	return mergeRichCardsWithLive(cards.map(artifactCardToRich))
		.map(richCardToArtifact)
		.filter((c): c is TutorArtifactCard => c !== null);
}

/**
 * Pending/ready cards из меню (нет part в сообщении до sync) — для last assistant.
 * knownJobRefs — уже показанные в ленте (не дублировать).
 */
export function orphanArtifactCards(knownJobRefs: Iterable<string>): TutorArtifactCard[] {
	return orphanRichCards(knownJobRefs)
		.map(richCardToArtifact)
		.filter((c): c is TutorArtifactCard => c !== null);
}

/** Live-merge + orphan attach со sticky-якорем (legacy artifactCards field). */
export function applyLiveArtifactCardsToMessages<
	T extends {
		id?: string;
		role: string;
		artifactCards?: TutorArtifactCard[];
		richCards?: TutorRichCard[];
	},
>(list: T[], extraKnownJobRefs: Iterable<string> = []): T[] {
	// Сначала richCards path (якоря + orphans), затем зеркалим artifact orphans
	// в legacy-поле — тем же sticky-якорем, без «всегда last assistant».
	for (const m of list) {
		if (m.role !== "assistant" || !m.id) continue;
		for (const c of m.artifactCards ?? []) bindRichCardAnchor(c.jobRef, m.id);
		for (const c of m.richCards ?? []) {
			if (c.kind === "artifact") bindRichCardAnchor(c.jobRef ?? c.id, m.id);
		}
	}
	const withRich = applyLiveRichCardsToMessages(list, extraKnownJobRefs);
	const knownRefs = new Set<string>(extraKnownJobRefs);
	for (const m of withRich) {
		for (const c of m.artifactCards ?? []) knownRefs.add(c.jobRef);
		for (const c of m.richCards ?? []) {
			if (c.kind === "artifact") knownRefs.add(c.jobRef ?? c.id);
		}
	}
	const orphans = orphanArtifactCards(knownRefs);
	if (orphans.length === 0) {
		return withRich.map((m) => {
			const mergedArtifacts =
				m.artifactCards && m.artifactCards.length > 0
					? mergeArtifactCardsWithLive(m.artifactCards)
					: m.artifactCards;
			return mergedArtifacts !== m.artifactCards ? { ...m, artifactCards: mergedArtifacts } : m;
		});
	}

	const byAnchor = new Map<string, TutorArtifactCard[]>();
	const unanchored: TutorArtifactCard[] = [];
	for (const card of orphans) {
		const anchor = getRichCardAnchor(card.jobRef);
		if (anchor) {
			const bucket = byAnchor.get(anchor) ?? [];
			bucket.push(card);
			byAnchor.set(anchor, bucket);
		} else {
			unanchored.push(card);
		}
	}

	const idToIdx = new Map<string, number>();
	for (let i = 0; i < withRich.length; i++) {
		const id = withRich[i]?.id;
		if (id) idToIdx.set(id, i);
	}
	// Мёртвый якорь — не fallback (см. card-live): иначе ghost после каждого ответа.
	for (const anchorId of [...byAnchor.keys()]) {
		if (!idToIdx.has(anchorId)) byAnchor.delete(anchorId);
	}
	// Уже «приземлённые» orphans не вешаем снова на last assistant.
	const fallback = unanchored.filter((c) => !isRichCardSettled(c.jobRef));
	let fallbackIdx = -1;
	if (fallback.length > 0) {
		for (let i = withRich.length - 1; i >= 0; i--) {
			const m = withRich[i];
			if (m?.role !== "assistant") continue;
			if ((m.artifactCards?.length ?? 0) > 0 || (m.richCards?.length ?? 0) > 0) {
				fallbackIdx = i;
				break;
			}
			if (fallbackIdx < 0 && m.id && !m.id.startsWith("voice-")) fallbackIdx = i;
		}
		if (fallbackIdx < 0) {
			for (let i = withRich.length - 1; i >= 0; i--) {
				if (withRich[i]?.role === "assistant") {
					fallbackIdx = i;
					break;
				}
			}
		}
	}

	return withRich.map((m, i) => {
		const mergedArtifacts =
			m.artifactCards && m.artifactCards.length > 0
				? mergeArtifactCardsWithLive(m.artifactCards)
				: m.artifactCards;
		const anchored = m.id ? (byAnchor.get(m.id) ?? []) : [];
		const extra = [...anchored, ...(i === fallbackIdx ? fallback : [])];
		if (extra.length > 0) {
			for (const c of extra) {
				if (m.id) bindRichCardAnchor(c.jobRef, m.id);
				markRichCardSettled(c.jobRef);
			}
			return { ...m, artifactCards: [...(mergedArtifacts ?? []), ...extra] };
		}
		if (mergedArtifacts !== m.artifactCards) {
			return { ...m, artifactCards: mergedArtifacts };
		}
		return m;
	});
}

/** @internal vitest only */
export function resetArtifactCardLiveForTests(): void {
	glossaryJobByMaterialId.clear();
	resetCardLiveForTests();
}
